/**
 * WebAudio backend for the Flash runtime's sound model (src/flash/media/Sound.ts).
 *
 * The runtime keeps every Flash-visible sound state (channels, positions, SOUND_COMPLETE, the
 * 32-channel limit, SoundMixer volume) on its own clock; this backend only makes the channels
 * audible:
 *  - files are fetched once and decoded with `decodeAudioData` (cached per URL);
 *  - `play()` returns a voice immediately; the source starts as soon as the buffer is decoded and
 *    the AudioContext runs. A late start (decode latency, autoplay-suspended context) skips the
 *    part that the runtime clock already "played", and a voice whose lifetime has passed is never
 *    started — so effects triggered before the first user gesture do not burst out on unlock,
 *    while looping music joins in at the right position;
 *  - loops follow Flash semantics: every pass plays from `startTime` to the end of the sound,
 *    `loops` passes in total (0 and 1 = once);
 *  - volume/pan through a GainNode + StereoPannerNode (pan ignored when unsupported);
 *  - MP3: the decoded buffer contains the encoder delay and padding (DefineSound declares fewer
 *    samples than the MP3 frames hold, and Flash skips `SeekSamples` at the start). An optional
 *    `leadInMs` in the request skips that delay; the runtime does not provide it yet (the manifest
 *    has no seekSamples), so MP3 sounds start with their encoder delay (the game's only MP3, the
 *    holy hand grenade, ~150 ms). UNVERIFIED against Flash Player output.
 * Call `resume()` from a user gesture (the browser input adapter does this) to unlock audio.
 */
import type { AudioBackend, AudioPlayRequest, AudioVoice } from './types';

/** Play request with the optional MP3 lead-in (ms of decoded audio to skip before `startTime`). */
export type AudioPlayRequestEx = AudioPlayRequest & { leadInMs?: number };

/**
 * How long a resume waits for a suspend in flight (ms). A suspend completes at once in every browser; the bound only
 * keeps a context that never answers from silencing the game for good.
 */
export const STATE_CHANGE_WAIT_MS = 1_000;

/** Structural subset of the Web Audio API used here (lets tests pass a fake context). */
export interface AudioContextLike {
  readonly currentTime: number;
  readonly state: string;
  readonly destination: any;
  createGain(): any;
  createStereoPanner?(): any;
  createBufferSource(): any;
  decodeAudioData(data: ArrayBuffer): Promise<any>;
  resume(): Promise<void>;
  suspend?(): Promise<void>;
  addEventListener?(type: string, cb: () => void): void;
}

export interface WebAudioBackendOptions {
  /** Existing context (default: a new AudioContext created on first use). */
  context?: AudioContextLike | null;
  /** Context factory (default: `new AudioContext()` / `webkitAudioContext`). */
  createContext?: () => AudioContextLike | null;
  /** Binary loader (default: fetch). */
  load?: (url: string) => Promise<ArrayBuffer>;
  /** Wall clock in ms (default: performance.now). Must match the runtime clock (platform.now). */
  now?: () => number;
  /** A late start of less than this many ms plays from the beginning instead of skipping. */
  lateStartToleranceMs?: number;
  onError?: (url: string, err: unknown) => void;
}

interface PendingVoice {
  req: AudioPlayRequest;
  requestedAt: number;
  stopped: boolean;
  started: boolean;
  source: any;
  gain: any;
  panner: any;
  volume: number;
  pan: number;
}

function defaultCreateContext(): AudioContextLike | null {
  const g: any = globalThis as any;
  const Ctor = g.AudioContext ?? g.webkitAudioContext;
  if (typeof Ctor !== 'function') return null;
  try {
    return new Ctor();
  } catch {
    return null;
  }
}

async function defaultLoad(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
  return res.arrayBuffer();
}

export class WebAudioBackend implements AudioBackend {
  private ctx: AudioContextLike | null;
  private readonly createContext: () => AudioContextLike | null;
  private readonly load: (url: string) => Promise<ArrayBuffer>;
  private readonly now: () => number;
  private readonly tolerance: number;
  private readonly onError: ((url: string, err: unknown) => void) | null;
  private readonly buffers = new Map<string, Promise<any | null>>();
  private readonly decoded = new Map<string, any | null>();
  private readonly waiting = new Set<PendingVoice>();
  private master: any = null;
  private masterVolume = 1;
  private muted = false;

  constructor(opts: WebAudioBackendOptions = {}) {
    this.ctx = opts.context ?? null;
    this.createContext = opts.createContext ?? defaultCreateContext;
    this.load = opts.load ?? defaultLoad;
    this.now = opts.now ?? (() => performance.now());
    this.tolerance = opts.lateStartToleranceMs ?? 60;
    this.onError = opts.onError ?? null;
  }

  /** The AudioContext (created lazily), or null when Web Audio is unavailable. */
  get context(): AudioContextLike | null {
    if (!this.ctx) {
      this.ctx = this.createContext();
      if (this.ctx && this.ctx.addEventListener) this.ctx.addEventListener('statechange', () => this.flushWaiting());
    }
    return this.ctx;
  }

  private output(): any {
    const ctx = this.context;
    if (!ctx) return null;
    if (!this.master) {
      this.master = ctx.createGain();
      this.master.gain.value = this.muted ? 0 : this.masterVolume;
      this.master.connect(ctx.destination);
    }
    return this.master;
  }

  /** Unlock/resume audio output (call from a user gesture). */
  async resume(): Promise<void> {
    const ctx = this.context;
    if (!ctx) return;
    this.wantRunning = true;
    // `state` still says 'running' until a suspend in flight completes: wait for it (bounded), then resume.
    if (this.suspending) await Promise.race([this.suspending, new Promise((r) => setTimeout(r, STATE_CHANGE_WAIT_MS))]);
    if (this.ctx !== ctx || !this.wantRunning) return; // a later suspend won
    if (ctx.state !== 'running') {
      // Never queued behind another resume: one that the browser keeps pending (a touch's pointerdown is not a user
      // activation, so autoplay leaves it unanswered) must not hold back the next gesture's resume, which unlocks.
      this.resuming++;
      try {
        await ctx.resume();
      } catch {
        /* still locked: retried on the next gesture */
      } finally {
        this.resuming--;
      }
    }
    this.flushWaiting();
  }

  /** Suspend audio output (the app went to the background); `resume()` continues it. */
  async suspend(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx || typeof ctx.suspend !== 'function') return;
    this.wantRunning = false;
    // A resume in flight leaves `state` at 'suspended' until it completes: suspend anyway (the context runs its
    // resume and suspend in the order they were called), never wait for a resume that may stay pending.
    if (ctx.state !== 'running' && this.resuming === 0) return;
    const op = (async () => {
      try {
        await ctx.suspend!();
      } catch {
        /* closed or not allowed: nothing plays anyway */
      }
    })();
    this.suspending = op;
    try {
      await op;
    } finally {
      if (this.suspending === op) this.suspending = null;
    }
  }

  /** The output state last asked for (resume: true, suspend: false). */
  private wantRunning = true;
  /** The suspend in flight (a resume waits for it), and the number of resumes in flight (a suspend does not wait). */
  private suspending: Promise<void> | null = null;
  private resuming = 0;

  /** Host-level master volume (0..1), independent of the game's SoundMixer. */
  setMasterVolume(v: number): void {
    this.masterVolume = Math.max(0, Math.min(1, +v || 0));
    if (this.master) this.master.gain.value = this.muted ? 0 : this.masterVolume;
  }

  setMuted(m: boolean): void {
    this.muted = !!m;
    if (this.master) this.master.gain.value = this.muted ? 0 : this.masterVolume;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  /** Fetch + decode a sound ahead of time. */
  preload(url: string): Promise<void> {
    return this.buffer(url).then(() => undefined);
  }

  private buffer(url: string): Promise<any | null> {
    let p = this.buffers.get(url);
    if (!p) {
      p = (async () => {
        const ctx = this.context;
        if (!ctx || !url) return null;
        try {
          const data = await this.load(url);
          const buf = await ctx.decodeAudioData(data);
          this.decoded.set(url, buf);
          return buf;
        } catch (err) {
          this.decoded.set(url, null);
          if (this.onError) this.onError(url, err);
          // eslint-disable-next-line no-console
          else console.warn(`[flash] sound ${url} could not be decoded`, err);
          return null;
        }
      })();
      this.buffers.set(url, p);
    }
    return p;
  }

  play(req: AudioPlayRequest): AudioVoice {
    const v: PendingVoice = {
      req: { ...req },
      requestedAt: this.now(),
      stopped: false,
      started: false,
      source: null,
      gain: null,
      panner: null,
      volume: req.volume,
      pan: req.pan,
    };
    const voice: AudioVoice = {
      stop: () => {
        if (v.stopped) return;
        v.stopped = true;
        this.waiting.delete(v);
        if (v.source) {
          try {
            v.source.stop();
          } catch {
            /* already stopped */
          }
          try {
            v.source.disconnect();
          } catch {
            /* ignore */
          }
        }
      },
      setVolume: (volume: number, pan: number) => {
        v.volume = volume;
        v.pan = pan;
        if (v.gain) v.gain.gain.value = Math.max(0, volume);
        if (v.panner) v.panner.pan.value = Math.max(-1, Math.min(1, pan));
      },
    };
    if (!req.url || !this.context) return voice;
    this.waiting.add(v);
    const cached = this.decoded.get(req.url);
    if (cached !== undefined) this.tryStart(v, cached);
    else void this.buffer(req.url).then((buf) => this.tryStart(v, buf));
    return voice;
  }

  private flushWaiting(): void {
    for (const v of [...this.waiting]) {
      const buf = this.decoded.get(v.req.url);
      if (buf !== undefined) this.tryStart(v, buf);
    }
  }

  /** Start `v` if possible (buffer decoded + context running), skipping already elapsed time. */
  private tryStart(v: PendingVoice, buf: any | null): void {
    if (v.stopped || v.started) {
      this.waiting.delete(v);
      return;
    }
    if (!buf) {
      this.waiting.delete(v);
      return;
    }
    const ctx = this.context;
    if (!ctx) return;
    if (ctx.state !== 'running') return; // resumed later through statechange / resume()
    this.waiting.delete(v);
    const lead = Math.max(0, +((v.req as AudioPlayRequestEx).leadInMs ?? 0) || 0) / 1000;
    const plan = planPlayback(buf.duration, lead + v.req.startTime / 1000, v.req.loops, (this.now() - v.requestedAt) / 1000, this.tolerance / 1000);
    if (!plan) {
      v.stopped = true; // its whole lifetime passed before it could start
      return;
    }
    const out = this.output();
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const gain = ctx.createGain();
    gain.gain.value = Math.max(0, v.volume);
    let node: any = gain;
    if (typeof ctx.createStereoPanner === 'function') {
      const p = ctx.createStereoPanner();
      p.pan.value = Math.max(-1, Math.min(1, v.pan));
      gain.connect(p);
      node = p;
      v.panner = p;
    }
    node.connect(out);
    src.connect(gain);
    if (plan.loop) {
      src.loop = true;
      src.loopStart = plan.loopStart;
      src.loopEnd = buf.duration;
    }
    const t = ctx.currentTime;
    src.start(t, plan.offset, plan.loop ? undefined : plan.duration);
    if (plan.loop) src.stop(t + plan.duration);
    src.onended = () => {
      try {
        src.disconnect();
        node.disconnect();
      } catch {
        /* ignore */
      }
    };
    v.source = src;
    v.gain = gain;
    v.started = true;
  }
}

/**
 * Playback plan for a sound of `dur` seconds started at `start` seconds with `loops` passes when
 * `elapsed` seconds already passed on the runtime clock. Returns null when nothing remains.
 * `offset`: where to begin in the buffer; `duration`: total remaining play time (all passes);
 * `loop`/`loopStart`: native looping from `start` to the end of the buffer.
 */
export function planPlayback(
  dur: number,
  start: number,
  loops: number,
  elapsed: number,
  tolerance = 0.06,
): { offset: number; duration: number; loop: boolean; loopStart: number } | null {
  const passes = Math.max(1, loops | 0);
  const s = Math.max(0, Math.min(start, dur));
  const per = dur - s;
  if (per <= 0) return null;
  const total = per * passes;
  const skip = elapsed <= tolerance ? 0 : elapsed;
  if (skip >= total) return null;
  const remaining = total - skip;
  const offset = s + (skip % per);
  return { offset, duration: remaining, loop: passes > 1, loopStart: s };
}
